import moment from 'moment';
import { IFAAAirspaceTimesheet } from '../model/authority-tiles/IActiveAirspaceProperties';
import 'moment-timezone';

export interface IActiveWindow {
    startTime: string;
    endTime: string;
}

const timeRefenceToTimeZoneWithDST = {
    'UTC-4': 'America/Moncton',
    'UTC-5': 'US/Eastern',
    'UTC-6': 'US/Central',
    'UTC-7': 'US/Mountain',
    'UTC-8': 'US/Pacific',
    'UTC-9': 'US/Alaska'
};

const timeRefenceToTimeZoneWithoutDST = {
    'UTC-4': 'America/Barbados',
    'UTC-5': 'Jamaica',
    'UTC-6': 'Etc/GMT+6',
    'UTC-7': 'US/Arizona',
    'UTC-8': 'Etc/GMT+8',
    'UTC-9': 'Etc/GMT+9'
};

export class TimesheetMaterializer {
    // given a mission start / end and timesheets, materialze the active windows that it is active
    materialize = ({ windowToMaterialize, timesheets }: { timesheets: IFAAAirspaceTimesheet[], windowToMaterialize: { startTime: string, endTime: string }}): IActiveWindow[] => {
        const activeTimes: IActiveWindow[] = [...this.materializeGenerator({ windowToMaterialize, timesheets })];
        return this.mergeOverlap(activeTimes);
    }

    public *materializeGenerator({ windowToMaterialize, timesheets }: { timesheets: IFAAAirspaceTimesheet[], windowToMaterialize: { startTime: string, endTime: string } }) {
        // this is likely way more than we need, but reduces FUD considering
        // UTC vs local time
        // daylight savings time
        // mission timezone vs airspace timezone (when on an airspace boundary)
        // note windowToMaterialize.startTime (and endTIme) are UTC dates, but we're going to use them as airspace localized dates
        // hence we're being overly broad here to limit the risk associated with that (by also getting the day before and after)
        const firstDate = moment(windowToMaterialize.startTime).add(-1, 'days').format('yyyy-MM-DD');
        const lastDate = moment(windowToMaterialize.endTime).add(1, 'days').format('yyyy-MM-DD');

        let dateCursor: string = firstDate;
        while (dateCursor <= lastDate) {
            const date = dateCursor;


            for (const { timeReference, startDate, endDate, startTime, endTime, daylightSavingAdjust, day } of timesheets) {
                // is this a date that is active?
                const activeStartDate = this.faaScheduleDateToISODate({ faaScheduleDate: startDate, date });
                const activeEndDate = this.faaScheduleDateToISODate({ faaScheduleDate: endDate, date });
                const isDateActive = date >= activeStartDate && date <= activeEndDate;
                if (!isDateActive) continue;

                // is this a day that is active?
                const timezoneId = this.getTimezone({ daylightSavingAdjust, timeReference });
                const dayOfWeek = moment.tz(date, timezoneId).format('ddd').toUpperCase();
                const isRelevantDay = day === 'ANY' || day === dayOfWeek;
                if (!isRelevantDay) continue;

                // compute the start and end time using the date, offset, and dst
                const startISOTimeWithOffset = `${date}T${startTime}-0${this.getOffset({ timeReference, daylightSavingAdjust, time: startTime, date })}:00`;
                const endISOTimeWithOffset = `${date}T${endTime}-0${this.getOffset({ timeReference, daylightSavingAdjust, time: endTime, date })}:00`;
                const activeStartTime = moment(startISOTimeWithOffset).toISOString();
                const activeEndTime = moment(endISOTimeWithOffset).toISOString();

                // only add if it is relevant to the mission
                const isActiveDuringMission = activeStartTime < windowToMaterialize.endTime && activeEndTime > windowToMaterialize.startTime;
                if (isActiveDuringMission) {
                    yield {
                        startTime: moment(startISOTimeWithOffset).toISOString(),
                        endTime: moment(endISOTimeWithOffset).toISOString()
                    };
                }
            }

            dateCursor = moment(dateCursor).add(1, 'day').format('yyyy-MM-DD');

        }

    }

    /**
     * if two windows overlap, then merge then into one window
     * eg 0100 to 0200 and 0200 to 0300 should become 0100 to 0300
     */
    private mergeOverlap = (activeTimes: IActiveWindow[]): IActiveWindow[] => {
        const mergedActiveTimes: IActiveWindow[] = [];
        for (const active of activeTimes) {
            // is there a schedule, that is active when mine starts
            const activeAtMyStart = mergedActiveTimes.find(s => {
                // which means it started <= my start, and ended >= my start
                return s.startTime <= active.startTime && s.endTime >= active.startTime;
            });

            const activeAtMyEnd = mergedActiveTimes.find(s => {
                // which means it started <= my end, and ended >= my end
                return s.startTime <= active.endTime && s.endTime >= active.endTime;
            });
            // if active at my start, and I end later
            if (activeAtMyStart && active.endTime > activeAtMyStart.endTime){
                activeAtMyStart.endTime = active.endTime;
            }
            else if (activeAtMyStart){
                // do nothing since I am already covered
            }
            // if active at my end, and I start sooner
            else if (activeAtMyEnd && active.startTime < activeAtMyEnd.startTime){
                activeAtMyEnd.startTime = active.startTime;
            }
            else if (activeAtMyEnd) {
                // do nothing since I am already covered
            }
            else {
                mergedActiveTimes.push(active);
            }
        }

        return mergedActiveTimes;
    }


    private faaScheduleDateToISODate = ({ faaScheduleDate, date }: {faaScheduleDate: string, date: string }): string => {
        const year = date.substring(0, 4);
        const [dayPart, monthPart] = faaScheduleDate.split('-');
        return `${year}-${monthPart}-${dayPart}`;
    }

    private getTimezone = ({timeReference, daylightSavingAdjust }: { timeReference: string, daylightSavingAdjust: 'YES' | 'NO' }): string => {
        const table = daylightSavingAdjust === 'YES' ? timeRefenceToTimeZoneWithDST : timeRefenceToTimeZoneWithoutDST;
        const result = table[timeReference];
        if (!result){
            throw new Error(`failed to find a match for ${timeReference}`);
        }
        return result;
    }

    private getOffset = ({ timeReference, daylightSavingAdjust, time, date }: { timeReference: string, daylightSavingAdjust: 'YES' | 'NO', time: string, date: string }): number => {
        const standardTimeOffset = Number(timeReference.split('UTC-')[1]);
        const dstOffset = standardTimeOffset - 1;
        if (daylightSavingAdjust === 'NO'){
            return standardTimeOffset;
        }

        // would have loved to let moment do more of this... but chicken and egg
        // I can't get something meaningful to moment, because I don't know which offset to use

        const nonYearDatePart = date.substring(5);
        const isFirstDay = nonYearDatePart === '03-13';
        if (isFirstDay && time < '02:00'){
            return standardTimeOffset;
        } else if (isFirstDay) {
            return dstOffset;
        }

        const isLastDay = nonYearDatePart === '11-06';
        if (isLastDay && time < '02:00'){
            return dstOffset;
        } else if (isLastDay) {
            return standardTimeOffset;
        }

        const isDuringDST = nonYearDatePart >= '03-13' && nonYearDatePart <= '11-06';
        if (isDuringDST){
            return dstOffset;
        } else {
            return standardTimeOffset;
        }
    }
}
